/**
 * Industry website concepts. Each entry powers:
 *  - A thumbnail card on the homepage.
 *  - A full preview page at /concepts/:slug rendered with the layout
 *    variant assigned in `layout`, in that industry's colors + voice.
 *
 * Layouts: service-pro | editorial-calm | premium-authority | portfolio
 */

export const concepts = [
  {
    slug: "apex-roofing",
    name: "Apex Roofing",
    industry: "Roofing & Exteriors",
    short: "Professional roofing company focused on lead generation and quote requests.",
    domain: "apexroofing.com",
    layout: "service-pro",
    theme: {
      bg: "#0F1A24", surface: "#142231", accent: "#E8843A", accentSoft: "#FFE0C4",
      text: "#F4F1EA", muted: "#9FB1C2",
      heroBg: "linear-gradient(135deg,#0F1A24 0%,#1B3045 100%)",
      thumbBg: "linear-gradient(135deg,#0F1A24 0%,#1B3045 100%)",
      fontDisplay: "Outfit", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=1600&q=80&auto=format&fit=crop",
      secondary: "https://images.unsplash.com/photo-1448630360428-65456885c650?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "Trusted roofers · Serving the Tri-State since 2008",
      title: "Roofs done right the first time.",
      sub: "Free, no-pressure quotes within 24 hours. Insurance-claim experts. Lifetime workmanship warranty.",
      ctaPrimary: "Get a free quote",
      ctaSecondary: "Call (555) 010-0123",
    },
    stats: [
      { k: "2,400+", v: "Roofs replaced" },
      { k: "24 hr",  v: "Quote turnaround" },
      { k: "Lifetime", v: "Workmanship warranty" },
      { k: "5.0 ★",  v: "Google rating" },
    ],
    services: [
      { title: "Residential Replacement", text: "Architectural shingles, metal, and tile with lifetime workmanship warranties." },
      { title: "Storm & Hail Damage",     text: "We work directly with your insurance and document every step of the claim." },
      { title: "Repairs & Maintenance",   text: "Same-week service for leaks, missing shingles, and flashing issues." },
      { title: "Gutters & Skylights",     text: "Seamless gutter installs, leaf guards, and skylight replacements." },
    ],
    valueProps: ["Free 24-hour quotes", "Insurance-claim experts", "Licensed & fully insured", "Lifetime workmanship warranty"],
    testimonial: { quote: "Apex handled our hail claim from start to finish. New roof in 9 days, zero stress.", author: "Linda M., Homeowner" },
    finalCta: "Schedule your free roof inspection",
  },

  {
    slug: "bluewave-hvac",
    name: "BlueWave HVAC",
    industry: "Heating, Cooling & Air Quality",
    short: "Modern HVAC company website focused on service calls and maintenance plans.",
    domain: "bluewavehvac.com",
    layout: "modern-tech",
    theme: {
      bg: "#F4F9FC", surface: "#FFFFFF", accent: "#0E76FB", accentSoft: "#DCEBFD",
      text: "#0A1828", muted: "#5C7185",
      heroBg: "linear-gradient(135deg,#F4F9FC 0%,#E2EFFB 100%)",
      thumbBg: "linear-gradient(135deg,#F4F9FC 0%,#C6DEF7 100%)",
      fontDisplay: "Outfit", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80&auto=format&fit=crop",
      secondary: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?w=1200&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "Same-day service · 24/7 emergency",
      title: "Comfort, restored — fast.",
      sub: "AC, heating, and indoor air-quality experts. Transparent pricing, certified techs, and a membership that pays for itself.",
      ctaPrimary: "Book a service call",
      ctaSecondary: "Join the comfort club",
    },
    stats: [
      { k: "90 min", v: "Avg arrival time" },
      { k: "24/7", v: "Emergency service" },
      { k: "100%", v: "Satisfaction guarantee" },
      { k: "4.9 ★", v: "1,200+ reviews" },
    ],
    steps: [
      { n: "01", title: "Book online",        text: "Pick a time that fits — confirmation in 60 seconds." },
      { n: "02", title: "Tech on the way",    text: "Real-time tracking, photo & name texted to you." },
      { n: "03", title: "Fixed, with a smile",text: "Up-front quote, clean workspace, satisfaction guaranteed." },
    ],
    tiers: [
      { name: "Essential",  price: "$19", per: "/mo", features: ["1 annual tune-up","Filter delivery","Priority booking"] },
      { name: "Comfort+",   price: "$39", per: "/mo", features: ["2 tune-ups/year","10% repair discount","No overtime fees"], featured: true },
      { name: "Total Care", price: "$79", per: "/mo", features: ["Unlimited diagnostics","20% repair discount","Whole-home air quality"] },
    ],
    services: [
      { title: "AC Repair & Install",     text: "All major brands. Up-front pricing before we lift a wrench." },
      { title: "Heating & Furnace",       text: "Gas, electric, heat pumps. Free second-opinion estimates." },
      { title: "Indoor Air Quality",      text: "HEPA, UV, and humidifiers — breathe better in every room." },
      { title: "Comfort Club Membership", text: "2 tune-ups/year, priority scheduling, 15% off all repairs." },
    ],
    valueProps: ["Same-day appointments", "Up-front, flat-rate pricing", "Licensed, background-checked techs", "100% satisfaction guarantee"],
    testimonial: { quote: "Tech arrived in 90 minutes, fixed our AC in one visit, and the price matched the quote exactly.", author: "Marcus & Dana, Homeowners" },
    finalCta: "Schedule your service in 60 seconds",
  },

  {
    slug: "brightsmile-dental",
    name: "BrightSmile Dental",
    industry: "Dental Care",
    short: "Clean, trustworthy dental website focused on appointments and patient education.",
    domain: "brightsmiledental.com",
    layout: "editorial-calm",
    theme: {
      bg: "#F4FBFA", surface: "#FFFFFF", accent: "#2E9A8A", accentSoft: "#D5EFEA",
      text: "#0E2A2D", muted: "#5B7677",
      heroBg: "linear-gradient(180deg,#F4FBFA 0%,#E6F4F1 100%)",
      thumbBg: "linear-gradient(135deg,#E6F4F1 0%,#A8D6CD 100%)",
      fontDisplay: "Cormorant Garamond", fontBody: "Outfit",
    },
    images: {
      hero:      "https://customer-assets.emergentagent.com/job_custom-crm-sites/artifacts/oirbm0qx_731E9FAB-7912-42E6-8E87-83A044409F3A.png",
      secondary: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=1200&q=80&auto=format&fit=crop",
      portrait:  "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=900&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "Welcoming new patients · Most insurance accepted",
      title: "Modern dental care, gentle as it should be.",
      sub: "Family dentistry, cosmetic dentistry, and clear aligners — in a calm, anxiety-free practice.",
      ctaPrimary: "Book an appointment",
      ctaSecondary: "Take a virtual tour",
    },
    doctor: { name: "Dr. Maya Patel, DDS", title: "Founder · 18 yrs experience",
      bio: "After 12 years at a high-volume corporate practice, Dr. Patel opened BrightSmile to do dentistry the way patients deserve — unhurried, evidence-based, and warm. She's a Diplomate of the American Board of Dental Sleep Medicine." },
    services: [
      { title: "Family Dentistry",   text: "Cleanings, fillings, and check-ups for every age." },
      { title: "Cosmetic Dentistry", text: "Whitening, veneers, and full smile makeovers." },
      { title: "Clear Aligners",     text: "Discreet, removable, and faster than traditional braces." },
      { title: "Emergency Dental",   text: "Same-day appointments for chipped teeth, pain, and trauma." },
    ],
    valueProps: ["Most major insurances accepted", "Sedation options available", "Same-day emergency visits", "Five-star Google reviews"],
    testimonial: { quote: "I used to dread the dentist. BrightSmile changed that. Calm staff, gentle dentist, beautiful office.", author: "Priya S., Patient since 2021" },
    finalCta: "Book your first visit",
  },

  {
    slug: "summit-legal",
    name: "Summit Legal Group",
    industry: "Law Firm",
    short: "Premium law firm website designed to build authority and generate consultations.",
    domain: "summitlegalgroup.com",
    layout: "premium-authority",
    theme: {
      bg: "#0B1426", surface: "#111B30", accent: "#C9A85C", accentSoft: "#F1E2BB",
      text: "#F2EFE6", muted: "#A8B0C4",
      heroBg: "linear-gradient(160deg,#0B1426 0%,#142340 70%,#1A2E54 100%)",
      thumbBg: "linear-gradient(135deg,#0B1426 0%,#1A2E54 100%)",
      fontDisplay: "Cormorant Garamond", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1600&q=80&auto=format&fit=crop",
      secondary: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80&auto=format&fit=crop",
      portrait:  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "Trial-tested attorneys · Confidential consultation",
      title: "Counsel built on results.",
      sub: "Personal injury, business litigation, and estate planning. We take the cases other firms turn down — and win.",
      ctaPrimary: "Request a consultation",
      ctaSecondary: "Our case results",
    },
    results: [
      { k: "$312M",   v: "Recovered for clients" },
      { k: "94%",     v: "Cases settled or won" },
      { k: "30+ yrs", v: "Combined experience" },
      { k: "AV ★★★★★", v: "Martindale-Hubbell" },
    ],
    services: [
      { title: "Personal Injury",     text: "We don't get paid unless you do. Free case evaluation." },
      { title: "Business Litigation", text: "Contract disputes, partnerships, and commercial defense." },
      { title: "Estate Planning",     text: "Wills, trusts, and probate handled with discretion." },
      { title: "Family Law",          text: "Divorce, custody, and mediation — handled with care." },
    ],
    valueProps: ["No fee unless we win (PI)", "Confidential consultations", "30+ years combined experience", "Featured in the Bar Journal"],
    testimonial: { quote: "Summit recovered a settlement nearly 3× what the insurance company first offered. Worth every minute.", author: "R.T., Client" },
    finalCta: "Speak with an attorney today",
  },

  {
    slug: "evergreen-landscaping",
    name: "Evergreen Landscaping",
    industry: "Landscape Design & Build",
    short: "Local landscaping company focused on showcasing projects and generating estimates.",
    domain: "evergreenlandscape.co",
    layout: "portfolio",
    theme: {
      bg: "#F5F1E8", surface: "#FFFFFF", accent: "#355E3B", accentSoft: "#D7E1D2",
      text: "#1A2A20", muted: "#5C6B61",
      heroBg: "linear-gradient(135deg,#F5F1E8 0%,#E2E5D7 100%)",
      thumbBg: "linear-gradient(135deg,#E2E5D7 0%,#A9C4A0 100%)",
      fontDisplay: "Cormorant Garamond", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?w=1600&auto=compress&cs=tinysrgb",
      gallery: [
        "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=900&q=80&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=900&q=80&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1605146768851-eda79da39897?w=900&q=80&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1597211833712-5e41faa202ea?w=900&q=80&auto=format&fit=crop",
        "https://images.pexels.com/photos/280222/pexels-photo-280222.jpeg?w=900&auto=compress&cs=tinysrgb",
        "https://images.unsplash.com/photo-1598902108854-10e335adac99?w=900&q=80&auto=format&fit=crop",
      ],
    },
    hero: {
      eyebrow: "Award-winning design-build · 15+ years local",
      title: "Outdoor spaces you'll actually live in.",
      sub: "From front-yard refresh to full-property design. Plant warranties, professional installs, and a portfolio to prove it.",
      ctaPrimary: "Request a free estimate",
      ctaSecondary: "See our portfolio",
    },
    services: [
      { title: "Landscape Design",     text: "Master plans, 3D renderings, and plant selection." },
      { title: "Hardscapes & Patios",  text: "Paver patios, retaining walls, fire pits, outdoor kitchens." },
      { title: "Lawn Care Programs",   text: "Year-round fertilization, weed control, and aeration." },
      { title: "Irrigation & Drainage",text: "Smart sprinklers, drip systems, and yard drainage." },
    ],
    valueProps: ["Free design consultations", "3-year plant warranty", "Licensed & insured crews", "Award-winning portfolio"],
    testimonial: { quote: "Evergreen turned an empty backyard into our favorite room of the house. Every neighbor stops to ask.", author: "The Petersons" },
    finalCta: "Start your outdoor project",
  },

  {
    slug: "northstar-accounting",
    name: "NorthStar Accounting",
    industry: "Accounting & Tax",
    short: "Professional accounting firm focused on credibility and client acquisition.",
    domain: "northstaracct.com",
    layout: "premium-authority",
    theme: {
      bg: "#FAF8F2", surface: "#FFFFFF", accent: "#1F4D40", accentSoft: "#D6E2DE",
      text: "#0F2A24", muted: "#5A6E68",
      heroBg: "linear-gradient(180deg,#FAF8F2 0%,#EDE9DC 100%)",
      thumbBg: "linear-gradient(135deg,#EDE9DC 0%,#A9C4BB 100%)",
      fontDisplay: "Cormorant Garamond", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&q=80&auto=format&fit=crop",
      secondary: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1200&q=80&auto=format&fit=crop",
      portrait:  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=900&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "CPA-led · Small business specialists",
      title: "Accounting that grows with you.",
      sub: "Bookkeeping, tax strategy, and CFO services for owner-operated businesses doing $250k – $10M.",
      ctaPrimary: "Book a discovery call",
      ctaSecondary: "Download tax checklist",
    },
    results: [
      { k: "$2.4M+", v: "Client tax savings, 2024" },
      { k: "180+", v: "Active business clients" },
      { k: "12 yrs", v: "Avg client tenure" },
      { k: "98%", v: "Client retention" },
    ],
    services: [
      { title: "Bookkeeping",         text: "Monthly close, clean books, dashboards you'll actually read." },
      { title: "Tax Strategy",        text: "Quarterly planning, never just filing. Save more, sleep better." },
      { title: "Fractional CFO",      text: "Cash-flow forecasting, KPI dashboards, board-ready reports." },
      { title: "Payroll & Sales Tax", text: "Set-and-forget compliance across multiple states." },
    ],
    valueProps: ["CPA-owned & operated", "Flat monthly pricing", "Secure client portal", "Industry-specialized advice"],
    testimonial: { quote: "NorthStar saved us $42,000 in our first year — and the books have never been cleaner.", author: "Owner, Regional HVAC Co." },
    finalCta: "Get clarity on your numbers",
  },

  {
    slug: "precision-plumbing",
    name: "Precision Plumbing",
    industry: "Plumbing Services",
    short: "Service-focused plumbing website designed to increase calls and bookings.",
    domain: "precisionplumbing.com",
    layout: "emergency-action",
    theme: {
      bg: "#FAF7F2", surface: "#FFFFFF", accent: "#DC2626", accentSoft: "#FEE2E2",
      text: "#1A0F0F", muted: "#5C5246",
      heroBg: "linear-gradient(180deg,#FAF7F2 0%,#F3E4D7 100%)",
      thumbBg: "linear-gradient(135deg,#FAF7F2 0%,#FCD6CC 100%)",
      fontDisplay: "Outfit", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1600&q=80&auto=format&fit=crop",
      secondary: "https://images.unsplash.com/photo-1542013936693-884638332954?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1620626011761-996317b8d101?w=1200&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "24/7 emergency plumbing · Up-front pricing",
      title: "On time. Cleaned up. Done right.",
      sub: "Drain cleaning, water heaters, leak repair, and full re-pipes. Local techs, transparent quotes, no surprises.",
      ctaPrimary: "Book a plumber",
      ctaSecondary: "Call (555) 010-7777",
    },
    stats: [
      { k: "60 min", v: "Avg dispatch time" },
      { k: "Flat", v: "Up-front pricing" },
      { k: "100%", v: "Workmanship guarantee" },
      { k: "5.0 ★", v: "Google rating" },
    ],
    phone: "(555) 010-7777",
    flatRates: [
      { service: "Service-call diagnostic",       price: "$49" },
      { service: "Drain unclog (standard)",       price: "$129" },
      { service: "Toilet repair or replace",      price: "from $249" },
      { service: "Water-heater install (40-gal)", price: "from $1,295" },
      { service: "Camera inspection",             price: "$199" },
      { service: "Leak detection (whole house)",  price: "$349" },
    ],
    services: [
      { title: "Drain Cleaning",      text: "Camera inspections, hydro-jetting, and stubborn-clog clearing." },
      { title: "Water Heaters",       text: "Tank, tankless, and hybrid installs — most done in a day." },
      { title: "Leak Detection",      text: "Acoustic + thermal tools. We find leaks without tearing up walls." },
      { title: "Re-pipes & Remodels", text: "Whole-house re-pipes, bathroom rough-ins, kitchen remodels." },
    ],
    valueProps: ["Up-front flat-rate pricing", "Licensed master plumbers", "Same-day appointments", "100% workmanship guarantee"],
    testimonial: { quote: "Quoted three plumbers. Precision was clearest, fastest, and stuck to the number. New water heater installed same day.", author: "Sarah K., Homeowner" },
    finalCta: "Book your plumber today",
  },

  {
    slug: "peak-performance-chiro",
    name: "Peak Performance Chiropractic",
    industry: "Chiropractic & Wellness",
    short: "Health and wellness website focused on appointment scheduling and patient trust.",
    domain: "peakperformancechiro.com",
    layout: "editorial-calm",
    theme: {
      bg: "#FAF5EE", surface: "#FFFFFF", accent: "#C2563B", accentSoft: "#F7D8CD",
      text: "#1F1612", muted: "#6B5C54",
      heroBg: "linear-gradient(180deg,#FAF5EE 0%,#F2E4D5 100%)",
      thumbBg: "linear-gradient(135deg,#F2E4D5 0%,#D49B81 100%)",
      fontDisplay: "Cormorant Garamond", fontBody: "Outfit",
    },
    images: {
      hero:      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1600&q=80&auto=format&fit=crop",
      secondary: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=80&auto=format&fit=crop",
      tertiary:  "https://images.unsplash.com/photo-1599043513900-ed6fe01d3833?w=1200&q=80&auto=format&fit=crop",
      portrait:  "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=900&q=80&auto=format&fit=crop",
    },
    hero: {
      eyebrow: "Now accepting new patients · Most insurance accepted",
      title: "Move better. Live stronger.",
      sub: "Evidence-based chiropractic, soft-tissue work, and rehab — for athletes, professionals, and anyone tired of being in pain.",
      ctaPrimary: "Schedule your visit",
      ctaSecondary: "New patient special",
    },
    doctor: { name: "Dr. Ethan Brooks, DC", title: "Founder · Sports Performance Certified",
      bio: "Dr. Brooks treats everyone from desk-job professionals to D1 athletes. He combines targeted adjustments with active rehab — so you don't keep coming back for the same pain." },
    services: [
      { title: "Chiropractic Adjustments", text: "Targeted, gentle adjustments tailored to your body." },
      { title: "Sports Rehab",             text: "Get back on the field — without re-injury." },
      { title: "Soft-Tissue Therapy",      text: "Active release, cupping, and dry needling." },
      { title: "Posture & Ergonomics",     text: "Desk-job pain plans for office and remote workers." },
    ],
    valueProps: ["Doctor-led care plans", "Same-week new-patient appointments", "Most major insurances accepted", "5.0 star average rating"],
    testimonial: { quote: "Six months of back pain — gone in three visits. I tell every co-worker about Peak.", author: "Jonathan R., Patient" },
    finalCta: "Book your first appointment",
  },
];

export const conceptBySlug = (slug) => concepts.find((c) => c.slug === slug);
